import { Model } from '@vuex-orm/core'
import Administration from '~/models/abstracts/Administration'
import AppNotifications from '~/services/Notifications/AppNotifications'
import ChainInheritance from '~/models/mixins/ChainInheritance'
import SoftDeletAble from '~/models/mixins/SoftDeletAble'
import PersistAble from '~/models/mixins/PersistAble'
import TimestampAble from '~/models/mixins/TimestampAble'
import Organization from '~/models/directories/Organization'
import Subscription from '~/modules/dashboard/models/Subscription'
import Billing from '~/models/billing/Billing'

export default class User extends ChainInheritance(Administration, [
  SoftDeletAble,
  TimestampAble,
  PersistAble
]) {
  static entity = 'users'
  static paginated = true
  static ormLoadWithRelations = true
  static defaultSortParam = 'dateCreated'
  static defaultSortOrder = true
  static persistOptions = {
    insertOrUpdate: ['organization']
  }

  static ROLES = {
    user: 'ROLE_USER',
    owner: 'ROLE_OWNER',
    superAdmin: 'ROLE_SUPER_ADMIN'
  }

  static ROLE_NAMES= {
    [User.ROLES.owner]: 'Власник',
    [User.ROLES.superAdmin]: 'Супер Адмін'
  }

  static fields () {
    return super.fields({
      id: this.attr(null),
      login: this.attr(null),
      roles: this.attr([]),
      promotion: this.attr(null), // todo: add relation for Promotion model when it was be created
      organizations: this.hasMany(Organization, 'owner_id'),
      profile: this.attr(null),
      subscriptions: this.hasMany(Subscription, 'id'),
      gatewayToken: this.attr(null),
      billing: this.attr(null)
    })
  }

  get name () {
    return this._.get(this, 'profile.name', '')
  }

  get promotionCode () {
    return this._.get(this, 'promotion.code', '-')
  }

  get rolesString () {
    let roles = ''
    this.roles.map((role, key) => {
      if (role !== User.ROLES.user) {
        roles += User.ROLE_NAMES[role] + ' '
      }
    })

    return roles || '-'
  }

  get billingStatus () {
    return this._.get(this.billing, 'status', '-')
  }

  get billingPayerCode () {
    return '#' + this._.get(this.billing, 'payerCode')
  }

  get organizationsNames () {
    let names = ''
    this.organizations.map((item, key) => {
      names += item.name + ','
    })

    return names
  }

  get isUser () {
    return this.roles.includes(User.ROLES.user)
  }

  get isSuperAdmin () {
    return this.roles.includes(User.ROLES.superAdmin)
  }

  static ormFilters = [
    {
      model: 'search',
      component: 'v-text-field',
      attrs: {
        outlined: true,
        'hide-details': true,
        placeholder: 'Search',
        'prepend-inner-icon': 'mdi-magnify'
      },
      classes: ['filled-input']
    }
  ]

  static ormFiltersConfig = {
    default: {
      grid: [
        {
          component: 'v-row',
          attrs: {
            justify: 'end'
          },
          nodes: [
            {
              component: 'v-col',
              attrs: {
                cols: 12
              },
              fields: [
                'search'
              ]
            }
          ]
        }
      ]
    }
  }

  static ormFieldsMap = {
    name: 'profile.name'
  }

  static ormTrans = {
    single: 'user',
    multy: 'users'
  }

  static ormHeaders = [
    { text: 'Name', value: 'name', sortable: false, filterable: false },
    { text: 'E-mail', value: 'profile.email', sortable: false, filterable: false },
    { text: 'Login ', value: 'login', sortable: false, filterable: false },
    { text: 'Phone', value: 'profile.phone', sortable: false, filterable: false },
    { text: 'Organizations', value: 'organizationsNames', sortable: false },
    { text: 'Actions', align: 'center', value: 'actions', width: '72', sortable: false, filterable: false }
  ]

  static getSortParams () {
    return super.getSortParams({
      'sort-by': ['active'],
      'sort-desc': [true]
    })
  }

  static ormFields = [
    {
      model: 'firstName',
      component: 'v-text-field',
      provider: {
        vid: 'firstName',
        name: 'First name',
        rules: 'required'
      },
      attrs: {
        label: 'First name',
        outlined: true
      }
    },
    {
      model: 'lastName',
      component: 'v-text-field',
      provider: {
        vid: 'lastName',
        name: 'Last name',
        rules: 'required'
      },
      attrs: {
        label: 'Last name',
        outlined: true
      }
    },
    {
      model: 'email',
      component: 'v-text-field',
      provider: {
        vid: 'email',
        name: 'E-mail',
        rules: 'required|email'
      },
      attrs: {
        label: 'E-mail (login)',
        type: 'email',
        outlined: true
      }
    },
    {
      model: 'login',
      component: 'v-text-field',
      provider: {
        vid: 'login',
        name: 'Login ',
        rules: 'required'
      },
      attrs: {
        label: 'Login ',
        type: 'text',
        outlined: true
      }
    },
    {
      model: 'phone',
      component: 'e-input-mask',
      provider: {
        vid: 'phone',
        name: 'Phone',
        rules: 'required|phone'
      },
      attrs: {
        label: 'Phone',
        mask: 'mobile',
        outlined: true
      }
    }
  ]

  static ormActions = [
    {
      name: 'login'
    },
    {
      name: 'edit'
    },
    {
      name: 'read',
      text: 'View',
      icon: {
        text: 'info',
        type: 'e-svg-icon'
      }
    },
    {
      name: 'delete',
      visible: item => !item.isSuperAdmin
    }
  ]

  static ormDialogs = {
    default: 'm-orm-crud-dialog',
    edit: 'm-orm-crud-dialog',
    read: 'm-orm-view-dialog',
    delete: 'm-orm-delete-dialog'
  }

  static ormDialogsConfig = {
    edit: {
      config: {
        context: 'update'
      }
    },
    read: {
      title: item => ({ name: item.name }),
      config: {
        context: 'read',
        fields: [
          {
            model: 'profile.firstName',
            label: 'First name'
          },
          {
            model: 'profile.lastName',
            label: 'Last name',
            value: val => val || '-'
          },
          {
            model: 'login',
            label: 'Login '
          },
          {
            model: 'email',
            label: 'Email',
            value: val => val || '-'
          },
          {
            model: 'phone',
            label: 'Phone',
            value: val => val || '-'
          },
          {
            model: 'promotionCode',
            label: 'Promotion'
          },
          {
            model: 'rolesString',
            label: 'Roles'
          },
          {
            model: 'billingPayerCode',
            label: 'Payer code'
          },
          {
            model: 'billingStatus',
            label: 'Billing status',
            actionButton: {
              text: 'Unblock',
              handle: async (item) => {
                try {
                  await Billing.api().activate(item.billing.id)
                  AppNotifications.success('Білінг користувача розблоковано.')
                } catch (e) {
                  AppNotifications.error(e)
                }
              },
              visible: (item) => {
                return (item.billing && (item.billing.status === Billing.STATUS_DISABLED || item.billing.status === Billing.STATUS_IN_DEBT))
              }
            }
          }
        ]
      }
    },
    delete: {
      title: item => ({ type: this.ormTrans.single, name: item.name }),
      notification: item => ({ type: this.ormTrans.single, name: item.name })
    }
  }

  static apiConfig = {
    get actions () {
      const configActions = Object.assign({}, Model.apiConfig.actions)
      configActions.addManagedOrganization = function (id, payload) {
        return this.put(
          Model.$routes.users.addManagedOrganization(id),
          payload
        )
      }
      configActions.getCurrent = function () {
        return this.get(Model.$routes.users.profile(), {
          dataKey: null,
          save: true,
          persistBy: 'create',
          persistOptions: {
            insertOrUpdate: ['organization']
          }
        })
      }
      configActions.generateGatewayToken = function () {
        return this.post(Model.$routes.users.gatewayToken())
      }
      configActions.getUserBilling = function () {
        return this.get(Model.$routes.users.billing())
      }
      configActions.updateUserProfile = function (payload) {
        return this.put(Model.$routes.users.profile(), payload)
      }
      configActions.updateUserPassword = function (payload) {
        return this.post(Model.$routes.users.changePassword(), payload)
      }
      return configActions
    }
  }
}
